<template>
  <div class="min-h-screen bg-background font-sans selection:bg-primary/10">
    <main class="pt-32 pb-20 container mx-auto px-4">
      <!-- <div class="max-w-4xl mx-auto"> -->
      <div class="max-w-5xl mx-auto">
        <transition name="fade-up" appear>
          <div class="text-center mb-16">
            <h1 class="text-4xl md:text-6xl font-serif text-primary mb-6">Commission a Portrait</h1>
            <p class="max-w-2xl mx-auto text-muted-foreground">
              Turn your cherished memories into timeless handmade art.
              Upload your photo, choose your style, and let us create a masterpiece for you.
            </p>
          </div>
        </transition>
        <div class="min-h-screen bg-gray-50 p-4 md:p-8 font-serif">
          <div class="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 items-start">

            <div class="w-full md:w-2/3 space-y-6">

              <div class="bg-white border border-gray-200 p-8 shadow-sm">
                <div class="flex justify-between items-start mb-6">
                  <div>
                    <h2 class="text-3xl font-light mb-2">Reference photo</h2>
                    <p class="text-gray-500 text-sm max-w-sm">Upload a clear, high-resolution image. For privacy, we
                      won't display it on the page.</p>
                  </div>
                  <button type="button"
                    class="border border-black px-6 py-2 text-sm hover:bg-gray-100 transition-colors"
                    @click="$refs.fileInput.click()">
                    Choose photo
                  </button>
                  <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileChange" />
                </div>
                <div
                  class="bg-gray-50 border border-dashed border-gray-300 p-4 flex justify-between items-center text-sm text-gray-500">
                  <img v-if="previewUrl" :src="previewUrl" class="mt-3 w-32 h-32 object-cover border" />
                  <span v-if="!previewUrl">No photo selected</span>

                  <span v-else class="text-green-700 font-medium">
                    Photo selected ✔
                  </span>
                  <span>Tip: good lighting + sharp focus.</span>
                </div>
              </div>

              <div class="bg-white border border-gray-200 p-8 shadow-sm">
                <div class="flex justify-between items-baseline mb-8">
                  <h2 class="text-3xl font-light">Available sizes & pricing</h2>
                  <span class="text-xs tracking-widest text-blue-900 font-sans uppercase">Standard US Sizes</span>
                </div>

                <div class="space-y-4">
                  <div v-for="size in SIZES" :key="size.id" @click="selectSize(size)"
                    class="border p-6 cursor-pointer transition-all duration-200" :class="selectedSize != null && selectedSize.id === size.id
                      ? 'border-black bg-gray-50'
                      : 'border-gray-200 bg-white hover:border-gray-400'">
                    <div class="flex justify-between">
                      <div>
                        <h3 class="text-xl mb-1">
                          {{ size.label }} ({{ size.dimension }})
                          — ${{ size.priceMin }}–${{ size.priceMax }}
                        </h3>

                        <p class="text-gray-400 text-sm mb-4">
                          {{ size.area }}
                        </p>

                        <ul v-if="size.highlights.length" class="space-y-2 text-sm text-gray-700">
                          <li v-for="(point, i) in size.highlights" :key="i" class="flex items-center gap-2">
                            <span class="text-purple-600">✔</span>
                            {{ point }}
                          </li>
                        </ul>
                      </div>

                      <div class="text-right">
                        <span class="text-xl font-light">
                          ${{ size.priceMin }}–${{ size.priceMax }}
                        </span>
                        <p class="text-[10px] text-gray-400 uppercase">
                          {{ size.currency }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-12 mb-12">

                    <section>
                      <div class="flex justify-between items-baseline mb-6 border-b border-gray-100 pb-2">
                        <h2 class="text-3xl font-serif text-gray-900 italic">What I offer</h2>
                        <span
                          class="text-[10px] tracking-widest text-blue-900 font-sans uppercase font-semibold">Portrait
                          Commissions</span>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="flex gap-4 p-6 border border-gray-200 bg-white">
                          <div
                            class="w-12 h-12 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                          </div>
                          <div>
                            <h3 class="font-bold text-gray-800 leading-tight">Couple & wedding portraits</h3>
                            <p class="text-gray-500 text-sm mt-1">Romantic, expressive, and gift-ready.</p>
                          </div>
                        </div>

                        <div class="flex gap-4 p-6 border border-gray-200 bg-white">
                          <div
                            class="w-12 h-12 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <div>
                            <h3 class="font-bold text-gray-800 leading-tight">Individual & family portraits</h3>
                            <p class="text-gray-500 text-sm mt-1">One person or a full family composition.</p>
                          </div>
                        </div>

                        <div class="flex gap-4 p-6 border border-gray-200 bg-white">
                          <div
                            class="w-12 h-12 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                            </svg>
                          </div>
                          <div>
                            <h3 class="font-bold text-gray-800 leading-tight">Anniversary & gift portraits</h3>
                            <p class="text-gray-500 text-sm mt-1">A meaningful, handmade present.</p>
                          </div>
                        </div>

                        <div class="flex gap-4 p-6 border border-gray-200 bg-white">
                          <div
                            class="w-12 h-12 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                            </svg>
                          </div>
                          <div>
                            <h3 class="font-bold text-gray-800 leading-tight">Watercolor + fine detail</h3>
                            <p class="text-gray-500 text-sm mt-1">Soft blends, clean lines, premium finish.</p>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section>
                      <div class="flex justify-between items-baseline mb-6">
                        <h2 class="text-xl font-serif text-gray-900 italic">How it works</h2>
                        <div
                          class="flex items-center gap-1 text-[10px] tracking-widest text-gray-400 font-sans uppercase font-semibold">
                          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
                          </svg>
                          Simple Process
                        </div>
                      </div>

                      <div class="space-y-3">
                        <div class="flex items-center gap-6 p-4 border border-gray-100 bg-white">
                          <div
                            class="w-14 h-14 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 class="font-bold text-gray-800">Send your reference photo</h4>
                            <p class="text-gray-500 text-sm">Upload + share special requests in the form.</p>
                          </div>
                        </div>

                        <div class="flex items-center gap-6 p-4 border border-gray-100 bg-white">
                          <div
                            class="w-14 h-14 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <h4 class="font-bold text-gray-800">Confirm quote & timeline</h4>
                            <p class="text-gray-500 text-sm">I'll respond by email to confirm pricing and turnaround.
                            </p>
                          </div>
                        </div>

                        <div class="flex items-center gap-6 p-4 border border-gray-100 bg-white">
                          <div
                            class="w-14 h-14 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </div>
                          <div>
                            <h4 class="font-bold text-gray-800">Hand-painted with care</h4>
                            <p class="text-gray-500 text-sm">Archival materials, professional artist-grade paint.</p>
                          </div>
                        </div>

                        <div class="flex items-center gap-6 p-4 border border-gray-100 bg-white">
                          <div
                            class="w-14 h-14 flex-shrink-0 border border-gray-100 bg-gray-50 flex items-center justify-center">
                            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          </div>
                          <div>
                            <h4 class="font-bold text-gray-800">Packed & shipped (USA)</h4>
                            <p class="text-gray-500 text-sm">Secure packaging + shipping included within the USA.</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                  <div class="space-y-12 mb-12">

                    <section>
                      <div class="flex justify-between items-start mb-6">
                        <div>
                          <h2 class="text-4xl font-serif text-blue-900 leading-tight">Commission Gallery</h2>
                          <p class="text-gray-500 text-sm mt-1">A few recent portrait commissions (examples).</p>
                        </div>
                        <!-- <a href="https://www.instagram.com/artpulse_by_shruti/"
                          class="text-sm font-sans pb-0.5 hover:text-gray-600 transition-colors">
                          View more on <br> <span class="font-bold">Instagram</span>
                        </a> -->
                        <div class="flex gap-4">
                          <a href="https://www.instagram.com/artpulse_by_shruti/" target="_blank" class="inline-flex items-center justify-center rounded-full 
             hover:bg-primary hover:text-black transition-colors 
             p-2 border border-gray-300">
                            <i class="fa-brands fa-instagram fs-24"></i>
                          </a>
                        </div>
                      </div>

                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div v-for="item in galleryItems" :key="item.id"
                          class="aspect-square bg-gray-200 overflow-hidden border border-gray-100 shadow-sm">
                          <img :src="item.small_fullurl" class="w-full h-full object-cover" />
                        </div>
                      </div>
                      <div v-if="!galleryItems.length" class="text-sm text-gray-400 italic">
                        Gallery coming soon…
                      </div>
                    </section>

                    <section class="bg-white border border-gray-200 p-8 shadow-sm">
                      <h3 class="text-2xl font-serif text-blue-900 mb-6">Client love</h3>
                      <div class="space-y-4 font-serif text-gray-700 italic text-lg">
                        <p class="leading-relaxed">
                          “Absolutely beautiful work. The painting captured our emotions perfectly.”
                        </p>
                        <p class="leading-relaxed">
                          “A truly special gift. Highly recommended!”
                        </p>
                      </div>
                    </section>

                  </div>
                </div>
              </div>
            </div>

            <div class="w-full md:w-1/3 sticky top-8 space-y-6">
              <div class="bg-white border border-gray-200 p-8 shadow-sm">
                <div class="flex justify-between items-baseline mb-6">
                  <h2 class="text-3xl font-light">Inquiry form</h2>
                  <span class="text-[10px] tracking-widest text-gray-400 font-sans">USA</span>
                </div>

                <form class="space-y-4 font-sans">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm mb-1">Name</label>
                      <input v-model="formData.name" type="text"
                        class="w-full border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-black">
                    </div>
                    <div>
                      <label class="block text-sm mb-1">Phone</label>
                      <input v-model="formData.phone_no" type="text"
                        class="w-full border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-black">
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm mb-1">Email</label>
                    <input v-model="formData.email" type="email"
                      class="w-full border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-black">
                  </div>
                  <div>
                    <label class="block text-sm mb-1">Special requests</label>
                    <textarea v-model="formData.instructions" rows="4"
                      placeholder="Background, clothing, details, number of people, etc."
                      class="w-full border border-gray-300 p-2 focus:outline-none focus:ring-1 focus:ring-black italic text-gray-400"></textarea>
                  </div>
                  <div class="">
                    <button type="button"
                      class="w-full text-lg py-3 tracking-widest uppercase bg-primary text-white  hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-3"
                      :disabled="isSubmitting" @click="handleSubmitCommissionPay">
                      <span v-if="!isSubmitting">Submit</span>
                      <span v-else class="flex items-center gap-3">
                        <svg class="w-5 h-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                          viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                          </circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Processing...
                      </span>
                    </button>


                  </div>
                </form>
              </div>

              <div class="bg-white border border-gray-200 p-6 shadow-sm">
                <h4 class="font-bold text-sm mb-2">Framing & shipping</h4>
                <p class="text-sm text-gray-600 leading-relaxed">
                  Shipping across the USA is included. Frames can be white, black, or natural wood (with or without
                  mat).
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </main>

    <!-- Simple Toast Notification -->
    <transition name="fade">
      <div v-if="toast.visible" class="fixed top-5 right-5 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50">
        <p class="font-bold">{{ toast.title }}</p>
        <p>{{ toast.description }}</p>
      </div>
    </transition>

    <!-- Loader overlay (reusable component) -->
    <LoaderOverlay :visible="isSubmitting" message="Processing your request" />

  </div>
</template>

<script>
import api from '@/services/api';
import { useUserStore } from '@/store/userStore';
import UploadMixin2 from '@/components/utilities/UploadMixin2.vue';
import ConstantPage from '@/components/utilities/ConstantPage.vue';
import LoaderOverlay from '@/components/utilities/LoaderOverlay.vue';
// import { capitalize } from 'vue';

// const SIZES = [
//   { id: 'x-small', label: 'Extra Small (A5 - 5x7")' },
//   { id: 'small', label: 'Small (A4 - 8x12")' },
//   { id: 'medium', label: 'Medium (A3 - 12x16")' },
//   { id: 'large', label: 'Large (A2 - 16x24")' },
//   { id: 'x-large', label: 'Extra Large (A1 - 20x30")' },
//   { id: 'custom', label: 'Custom Size' },
// ];

const SIZES = [
  {
    id: 'small', label: 'Small', dimension: '8×10 in',
    area: '80 sq in', priceMin: 80, priceMax: 100, currency: 'USD',
    highlights: [
      'Most popular US frame size',
      'Perfect for gifts & small spaces'
    ],
  },
  {
    id: 'medium', label: 'Medium', dimension: '9×12 in', area: '108 sq in',
    priceMin: 120, priceMax: 140, currency: 'USD',
    highlights: [
      'Very common for watercolor portraits',
      'Elegant and balanced look'
    ],
  },
  {
    id: 'large', label: 'Large', dimension: '11×14 in', area: '154 sq in',
    priceMin: 160, priceMax: 190, currency: 'USD',
    highlights: [
      'Premium & statement size',
      'Looks great with mat + frame'
    ],
  },
  {
    id: 'x-large', label: 'Extra Large', dimension: '12×16 in', area: '192 sq in',
    priceMin: 200, priceMax: 230, currency: 'USD',
    highlights: [
      'Available in US stores',
      'Best for anniversary & wedding portraits'
    ],
  }
];

const MEDIUMS = [
  { id: 'watercolor', label: 'Watercolor', description: 'Soft, fluid, and expressive.' },
  { id: 'charcoal', label: 'Charcoal / Pencil', description: 'Classic black and white realism.' },
  // { id: 'oil', label: 'Oil / Acrylic', description: 'Rich textures and vibrant colors.' },
];

export default {
  mixins: [UploadMixin2, ConstantPage],
  name: 'CommissionPage',
  components: {
    LoaderOverlay,
  },
  data() {
    return {
      SIZES,
      MEDIUMS,
      step: 1,
      selectedSize: null,
      // selectedMedium: null,
      galleryItems: [],

      isSubmitting: false,
      submitAfterUpload: false,
      formData: {
        name: '',
        email: '',
        phone_no: '',
        instructions: '',
      },
      toast: {
        visible: false,
        title: '',
        description: '',
      },
      // previewUrl: "https://gradmainbucket.s3.amazonaws.com/other/screenshot-from-2025-07-01-11-54-35-72615019-7bc9-4730-b0c3-f06a7fd0e167.png",
      // amazonid: 19964,
      previewUrl: null,
      amazonid: 0,
      price: 0,
      artworks: [],
      categories: ['All'],
      filter: 'All',
      showPurchaseForm: false,
      // selectedArtwork: null,
      currentImageIndex: 0,
      art_medium: 'Watercolor on Paper',
    };
  },
  computed: {
    estimatedPrice() {
      if (!this.selectedSize) return 0;
      return this.selectedSize.priceMax;
    },
    filteredArtworks() {
      if (this.filter === 'All') {
        return this.artworks;
      }

      return this.artworks.filter(art =>
        art.categories?.some(cat => cat.name === this.filter)
      );
    }
  },
  mounted() {
    this.fetchProducts();
    // this.fetchGalleryItems();
  },
  methods: {
    selectSize(size) {
      console.log('Selected size:', size);
      this.selectedSize = size;
    },
    handleFileChange(event) {
      let obj = {
        type: 5,
        directorypath: '',
        counter: 0,
        typeid: 0,
        typeint: 1,
        width: 1000,
        height: 1000
      };

      useUserStore().setfilecomponents(obj);
      this.processFileWithoutLogin(event); // 👈 from UploadMixin2
    },

    uploaded() {
      const uploadedimage = useUserStore().uploadedimage;

      this.previewUrl = uploadedimage.fullurl;
      this.amazonid = uploadedimage.awspresignedid;

      useUserStore().resetfilecomponents();

      // If form submission was triggered before upload completed, now submit after image upload succeeds
      if (this.submitAfterUpload) {
        this.submitAfterUpload = false;
        this.submitCommissionForm();
      }
    },
    openArtworkModal(artwork) {
      console.log('Opening artwork modal for:', artwork);
      this.selectedArtwork = artwork;
      this.currentImageIndex = 0;
      document.body.style.overflow = 'hidden';
    },
    closeArtworkModal() {
      this.showPurchaseForm = false;
      this.selectedArtwork = null;
      document.body.style.overflow = '';
    },
    nextImage() {
      const total = this.selectedArtwork.images.length;
      this.currentImageIndex = (this.currentImageIndex + 1) % total;
    },
    prevImage() {
      const total = this.selectedArtwork.images.length;
      this.currentImageIndex =
        (this.currentImageIndex - 1 + total) % total;
    },
    async handleSubmitCommissionPay() {
      // Validate size selection
      if (this.selectedSize == null) {
        this.showToast("Error", "Please select a size.");
        return;
      }

      // Validate image is selected
      // if (!this.previewUrl) {
      //   this.showToast("Error", "Please upload a reference photo before submitting.");
      //   return;
      // }

      // If image selected but not yet uploaded, initiate upload and set flag to submit after
      if (this.amazonid === 0) {
        this.submitAfterUpload = true;
        const file = this.$refs.fileInput?.files?.[0];
        if (file) {
          // Set up file components for upload
          let obj = {
            type: 5,
            directorypath: '',
            counter: 0,
            typeid: 0,
            typeint: 1,
            width: 1000,
            height: 1000
          };
          useUserStore().setfilecomponents(obj);
          // Create synthetic change event for upload
          const event = { target: { files: this.$refs.fileInput.files } };
          this.processFileWithoutLogin(event);
        }
        // return;
      }

      // Image already uploaded, proceed with form submission
      this.submitCommissionForm();
    },

    async submitCommissionForm() {
      this.isSubmitting = true;
      try {
        const payload = {
          ...this.formData,
          size_json: JSON.stringify(this.selectedSize),
          amazonid: this.amazonid ?? 0,
          image_url: this.previewUrl ?? null,
          price: this.estimatedPrice ?? 0,
        };

        console.log('payload:', payload);

        const response = await api.post('api/artpulse/commission/save', payload);

        if (response.data?.success) {
          this.step = 3; // success step
          this.showToast(
            "Commission Request Received",
            "We've received your photo and details. We will contact you shortly."
          );
        } else {
          throw new Error(response.data?.message || 'API returned failure');
        }

      } catch (error) {
        console.error('Commission API Error:', error);

        const message =
          error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again later.";

        this.showToast("Error", message);

      } finally {
        this.isSubmitting = false;
        this.resetForm();
      }
    },
    sendWhatsappCommissionForm() {

      if (this.formData.email == '' || this.formData.email == null) {
        this.showToast("Error", "Please enter your email and phone number.");
        return;
      }
      if (this.formData.phone_no == '' || this.formData.phone_no == null) {
        this.showToast("Error", "Please enter your email and phone number.");
        return;
      }

      this.isSubmitting = true;
      try {
        const data = {
          heading: "Commission Request",
          name: this.formData.name,
          email: this.formData.email,
          phone_no: this.formData.phone_no,
          instructions: this.formData.instructions,
          size_json: JSON.stringify({ label: this.selectedSize.label }),
          // art_medium_json: JSON.stringify({ label: this.selectedMedium.label }),
          amazonid: this.amazonid ?? 0,
          image_url: this.previewUrl ?? 'No image',

        }

        const size = JSON.parse(data.size_json).label
        const medium = JSON.parse(data.art_medium_json).label

        const message = `
                
🖼️ *${data.heading}*

👤 *Customer Details*
• Name: ${data.name}
• Email: ${data.email}
• Phone: ${data.phone_no}
• Instructions: ${data.instructions}

🎨 *Artwork Details*
• Size: ${size}
• Medium: ${medium}
• Image ID: ${data.amazonid}
• Image URL: ${data.image_url}

📎 Please attach the image again if needed.`

        const encodedMessage = encodeURIComponent(message.trim())

        window.open(
          `https://wa.me/14806360819?text=${encodedMessage}`,
          '_blank'
        )
      } catch (error) {
        console.error(error)
      } finally {
        this.isSubmitting = false;
        this.resetForm();
      }
    },
    async handleSubmitPurchase() {
      this.isSubmitting = true;
      try {
        const payload = {
          ...this.formData,
          size_json: JSON.stringify({ label: this.selectedArtwork.size }),
          art_medium_json: JSON.stringify({ label: this.art_medium }),
          amazonid: this.selectedArtwork.amazonid,
          image_url: this.selectedArtwork.src,
          price: this.selectedArtwork.price,
          productid: this.selectedArtwork.id,
          outofstock: this.selectedArtwork.outofstock,
        };

        console.log('payload:', payload)
        return;

        // const response = await api.post('api/artpulse/commission/save', payload);

        // if (response.data?.success) {
        //   this.showToast(
        //     "Order Request Received",
        //     `Thank you ${this.formData.name}! We will contact you shortly about "${this.selectedArtwork.title}".`
        //   );
        //   this.showPurchaseForm = false;
        // } else {
        //   throw new Error(response.data?.message || 'API returned failure');
        // }

      } catch (error) {
        console.error('purchase API Error:', error);

        const message =
          error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again later.";

        this.showToast("Error", message);

      } finally {
        this.showPurchaseForm = false;
        this.isSubmitting = false;
      }
    },
    sendWhatsappOnPurchase() {
      if (this.formData.email == '' || this.formData.email == null) {
        this.showToast("Error", "Please enter your email and phone number.");
        return;
      }
      if (this.formData.phone_no == '' || this.formData.phone_no == null) {
        this.showToast("Error", "Please enter your email and phone number.");
        return;
      }
      this.isSubmitting = true;
      try {
        const data = {
          heading: this.selectedArtwork.outofstock === 0 ? "Artwork Purchase Request" : "Artwork Re-order Request",
          productid: this.selectedArtwork.id,
          outofstock: this.selectedArtwork.outofstock,
          name: this.formData.name,
          email: this.formData.email,
          phone_no: this.formData.phone_no,
          address: this.formData.address,
          size_json: JSON.stringify({ label: this.selectedArtwork.size }),
          art_medium_json: JSON.stringify({ label: this.art_medium }),
          price: this.selectedArtwork.price,
          productTitle: this.selectedArtwork.title,
        }

        const size = JSON.parse(data.size_json).label
        const medium = JSON.parse(data.art_medium_json).label

        const message = `
                
*${data.heading}*

*Customer Details*
• Name: ${data.name}
• Email: ${data.email}
• Phone: ${data.phone_no}

*Shipping Address*
${data.address}

*Artwork Details*
• Title: ${data.productTitle}
• Price: ₹${data.price}
• Size: ${size}
• Medium: ${medium}
                `

        const encodedMessage = encodeURIComponent(message.trim())

        window.open(
          `https://wa.me/14806360819?text=${encodedMessage}`,
          '_blank'
        )
      } catch (error) {
        console.error(error)
      } finally {
        this.showPurchaseForm = false;
        this.isSubmitting = false;
        this.resetForm();
      }
    },

    resetForm() {
      this.step = 1;
      this.selectedSize = null;
      // this.selectedMedium = null;
      this.previewUrl = null;
      this.formData = { name: '', email: '', phone_no: '', instructions: '' };
      this.amazonid = 0;
      this.price = 0;
    },
    showToast(title, description) {
      this.toast.title = title;
      this.toast.description = description;
      this.toast.visible = true;
      setTimeout(() => {
        this.toast.visible = false;
      }, 5000);
    },
    async fetchProducts() {
      try {

        let param = {
          fetchColumns: 'id,name,cp,sp',
          category_name: 'Commission'
        }
        const response = await api.post('api/product/getwithcategories', param);

        const products = response.data?.data || [];

        // this.galleryItems = products.map(product => {
        //   product.product_category?.forEach(cat => {
        //     if (cat.name !== 'Gallery' && cat.name !== 'Commission') {
        //       categorySet.add(cat.name);
        //     }
        //   });

        //   let width = Number(product.width);
        //   let height = Number(product.height);
        //   let size = `${width} x ${height} ${capitalize(product.measurement_unit)}`;
        //   let status = product.outofstock === 0 ? 'available' : 'sold';

        //   return {
        //     id: product.id,
        //     title: product.name,
        //     price: product.sp,
        //     size,
        //     // images: product.productmedia?.map(m => m.small_fullurl) || [],
        //     images: product.productmedia?.[0]?.small_fullurl || [],
        //     amazonid: product.productmedia?.[0]?.small_amazonsignedid,
        //     status,
        //     outofstock: product.outofstock,
        //     // if product has multiple categories, use first for display
        //     category: product.product_category?.[1]?.name ?? 'Uncategorized',
        //     categories: product.product_category
        //   };
        // });
        this.galleryItems = products.map(product => {

          return {
            id: product.id,
            name: product.name,
            sp: product.sp,
            // images: product.productmedia?.map(m => m.small_fullurl) || [],
            small_fullurl: product.productmedia?.[0]?.small_fullurl || [],
            small_amazonsignedid: product.productmedia?.[0]?.small_amazonsignedid,
          };
        });
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    },
    async fetchGalleryItems() {
      try {
        let param = {
          companyid: this.COMPANY_ID,
          category_name: 'exhibition'
        }
        const response = await api.post('api/gallery/list', param);
        if (response.data?.data.length == 0) {
          return;
        }
        this.galleryItems = response.data?.data[0].gallery_media || [];
      } catch (error) {
        console.error('Failed to fetch gallery items:', error);
      }
    }
  },
  beforeUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.form-select,
.form-input,
textarea {
  background-color: #fff;
  border: 1px solid #000 !important;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 0.8s, transform 0.8s;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.upload-area {
  border: 2px dashed #e2e2e2 !important;
}

.border {
  border: 1px solid #e2e2e2 !important;
  box-shadow: 0px 2px 8px #e2e2e2;
}

.border-black {
  border: 1px solid #000 !important;
  /* box-shadow: 0px 2px 8px #000; */
}

.loader-ring {
  border: 6px solid rgba(59, 130, 246, 0.15);
  /* light primary */
  border-top-color: rgba(59, 130, 246, 1);
  /* primary */
  border-radius: 9999px;
  width: 80px;
  height: 80px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>